import { useMemo, useState } from "react"
import { AlertTriangle, GripVertical, Info, Plus, Trash2, X } from "lucide-react"
import { JsonEditor } from "@/components/ui/json-editor"
import { Button } from "@/components/ui/button"
import { Dialog } from "@/components/ui/dialog"
import { cn } from "@/lib/cn"

// Self-contained "Request Params Schema" card. Wraps a JSON string buffer (the
// same one the controller already accepts) and exposes a master/detail
// editor + a raw-JSON fallback for power users.
//
//   ┌────────────────────────────────────────────────────────────────┐
//   │ Request Params                        [Visual│Raw JSON][+ Add] │
//   │ Define the optional request parameters this endpoint accepts.  │
//   ├──────────────────────────┬─────────────────────────────────────┤
//   │ FIELDS (3)               │ Edit field            [trash]       │
//   │ Drag to reorder          │ name / type / format / required /   │
//   │ ┌──────────────────────┐ │ min/max / enum / pattern / desc     │
//   │ │ ⋮⋮ name [optional]  │ │                                      │
//   │ │    type              │ │                                      │
//   │ │    constraint hint   │ │                                      │
//   │ └──────────────────────┘ │                                      │
//   │ …                        │                                      │
//   ├──────────────────────────┴─────────────────────────────────────┤
//   │ ℹ Query fields are optional unless marked as required.         │
//   └────────────────────────────────────────────────────────────────┘
//
// Keep in lock-step with NormalizeQuerySchema's SUPPORTED_KEYS /
// SUPPORTED_TYPES / SUPPORTED_FORMATS — adding a keyword there means
// adding a control here.

type Param = {
  name: string
  type?: "string" | "integer" | "number" | "boolean" | ""
  format?: string
  enum?: unknown[]
  pattern?: string
  minimum?: number
  maximum?: number
  required?: boolean
  default?: unknown
  description?: string
  location?: "query" | "body" | ""
}

const TYPE_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "",        label: "(any)" },
  { key: "string",  label: "string" },
  { key: "integer", label: "integer" },
  { key: "number",  label: "number" },
  { key: "boolean", label: "boolean" },
]

const FORMAT_OPTIONS = [
  { key: "",          label: "—" },
  { key: "date-time", label: "date-time (ISO8601)" },
  { key: "date",      label: "date (YYYY-MM-DD)" },
  { key: "uuid",      label: "uuid" },
  { key: "email",     label: "email" },
  { key: "uri",       label: "http(s) url" },
]

const LOCATION_OPTIONS = [
  { key: "",      label: "default for method" },
  { key: "query", label: "query string" },
  { key: "body",  label: "JSON body" },
]

type Props = {
  value: string                     // canonical JSON string round-tripped through the controller
  onChange: (next: string) => void
  httpMethod?: string
}

function tryParse(value: string): Param[] | null {
  try {
    const parsed = JSON.parse(value || "[]")
    if (!Array.isArray(parsed)) return null
    return parsed.map((entry) => {
      if (typeof entry === "string") return { name: entry }
      if (entry && typeof entry === "object") return { ...(entry as Param) }
      return { name: "" }
    })
  } catch { return null }
}

function paramsToJson(params: Param[]): string {
  const stripped = params.map((p) => {
    const out: Record<string, unknown> = { name: p.name }
    if (p.type)                                                         out.type        = p.type
    if (p.format)                                                       out.format      = p.format
    if (p.enum && p.enum.length)                                        out.enum        = p.enum
    if (p.pattern)                                                      out.pattern     = p.pattern
    if (p.minimum !== undefined && !globalThis.Number.isNaN(p.minimum)) out.minimum     = p.minimum
    if (p.maximum !== undefined && !globalThis.Number.isNaN(p.maximum)) out.maximum     = p.maximum
    if (p.required === true)                                            out.required    = true
    if (p.default !== undefined && p.default !== "")                    out.default     = p.default
    if (p.description)                                                  out.description = p.description
    if (p.location)                                                     out.location    = p.location
    return out
  })
  return JSON.stringify(stripped, null, 2)
}

// One-line constraint hint shown in the left list — the most distinctive
// field on the param. Falls back to nothing if the param has just a name.
function summarize(p: Param): string | null {
  if (p.enum && p.enum.length)                  return `Enum: ${p.enum.join(" / ")}`
  if (p.pattern)                                return `Pattern: ${p.pattern}`
  if (p.format)                                 return `Format: ${p.format}`
  if (p.minimum !== undefined && p.maximum !== undefined) return `${p.minimum}..${p.maximum}`
  if (p.minimum !== undefined)                  return `Min: ${p.minimum}`
  if (p.maximum !== undefined)                  return `Max: ${p.maximum}`
  return null
}

export function QueryParamEditor({ value, onChange, httpMethod = "GET" }: Props) {
  const [tab, setTab] = useState<"visual" | "json">("visual")
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)

  const parsed       = useMemo(() => tryParse(value), [value])
  const visualUsable = parsed !== null
  const defaultParamLocation = httpMethod.toUpperCase() === "GET" ? "query" : "body"
  const defaultParamLabel = defaultParamLocation === "query" ? "query string" : "JSON body"

  function commit(next: Param[]) {
    onChange(paramsToJson(next))
  }

  function addField() {
    if (!parsed) { setTab("json"); return }
    const next = [ ...parsed, { name: "" } ]
    commit(next)
    setSelectedIdx(next.length - 1)
    if (tab !== "visual") setTab("visual")
  }

  function updateField(idx: number, patch: Partial<Param>) {
    if (!parsed) return
    const next = parsed.slice()
    next[idx] = { ...next[idx], ...patch }
    commit(next)
  }

  function removeField(idx: number) {
    if (!parsed) return
    const next = parsed.filter((_, i) => i !== idx)
    commit(next)
    setSelectedIdx((cur) => Math.max(0, Math.min(cur, next.length - 1)))
  }

  function moveField(from: number, to: number) {
    if (!parsed) return
    if (from === to || from < 0 || to < 0) return
    const next = parsed.slice()
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    commit(next)
    setSelectedIdx(to)
  }

  const safeSelected = parsed ? Math.min(selectedIdx, parsed.length - 1) : 0
  const selected     = parsed && parsed.length ? parsed[safeSelected] : null

  return (
    <div className="rounded-control border border-border bg-app-bg/40">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start gap-3 border-b border-border p-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-app-text">Request Params</h3>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="rounded-full p-0.5 text-app-subtle transition hover:bg-white/5 hover:text-app-text"
              title="What are request params?"
              aria-label="What are request params?"
            >
              <Info size={14} strokeWidth={2} />
            </button>
          </div>
          <p className="mt-0.5 text-xs text-app-muted">
            Define the inputs this endpoint accepts. Defaults go in the {defaultParamLabel}.
          </p>
        </div>
        <SegmentedToggle
          value={tab}
          onChange={(v) => setTab(v)}
          options={[
            { key: "visual", label: "Visual" },
            { key: "json",   label: "Raw JSON" },
          ]}
        />
        <Button
          variant="red"
          size="sm"
          onClick={addField}
          disabled={tab === "visual" && !visualUsable}
          type="button"
        >
          <Plus size={14} className="mr-1" /> Add field
        </Button>
      </div>

      {/* ── Body ────────────────────────────────────────────────── */}
      {tab === "json" && (
        <div className="p-3">
          <JsonEditor value={value} onChange={onChange} minHeight="240px" />
        </div>
      )}

      {tab === "visual" && !visualUsable && (
        <div className="flex items-center gap-2 p-4 text-sm text-status-error">
          <AlertTriangle size={14} />
          <span>JSON has errors — switch to <strong>Raw JSON</strong> to fix, then come back.</span>
        </div>
      )}

      {tab === "visual" && visualUsable && parsed.length === 0 && (
        <div className="p-6 text-center text-sm text-app-muted">
          No fields yet. Click <span className="font-medium text-app-text">+ Add field</span> to start.
        </div>
      )}

      {tab === "visual" && visualUsable && parsed.length > 0 && (
        <div className="grid gap-0 md:grid-cols-[minmax(0,260px)_1fr] md:divide-x md:divide-border">
          {/* Left — list of fields */}
          <div className="p-3">
            <div className="mb-1 text-[10px] uppercase tracking-wide text-app-subtle">Fields ({parsed.length})</div>
            <div className="mb-2 text-[11px] text-app-subtle">Drag to reorder</div>
            <ul className="space-y-1.5">
              {parsed.map((p, i) => (
                <li
                  key={i}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => { e.preventDefault(); setDragOverIdx(i) }}
                  onDragLeave={() => setDragOverIdx((cur) => (cur === i ? null : cur))}
                  onDrop={(e) => {
                    e.preventDefault()
                    if (dragIdx !== null) moveField(dragIdx, i)
                    setDragIdx(null); setDragOverIdx(null)
                  }}
                  onDragEnd={() => { setDragIdx(null); setDragOverIdx(null) }}
                  onClick={() => setSelectedIdx(i)}
                  className={cn(
                    "group flex cursor-pointer items-start gap-2 rounded-control border bg-app-bg/40 px-2.5 py-2 transition",
                    safeSelected === i
                      ? "border-brand-red/60 bg-brand-redSoft"
                      : "border-border hover:bg-white/5",
                    dragOverIdx === i && dragIdx !== i && "border-brand-red/40 bg-brand-redSoft/40",
                  )}
                >
                  <span className="mt-1 cursor-grab text-app-subtle group-hover:text-app-text" aria-hidden>
                    <GripVertical size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono text-xs font-semibold text-app-text">
                      {p.name || <span className="italic text-app-subtle">(unnamed)</span>}
                    </div>
                    <div className="mt-0.5 text-[11px] text-app-muted">{p.type || "any"}</div>
                    {summarize(p) && (
                      <div className="mt-0.5 truncate text-[11px] text-app-muted">{summarize(p)}</div>
                    )}
                  </div>
                  <span className={cn(
                    "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                    p.required
                      ? "bg-status-warning/20 text-status-warning"
                      : "bg-white/5 text-app-subtle",
                  )}>
                    {p.required ? "required" : "optional"}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — detail editor for selected field */}
          {selected && (
            <DetailEditor
              key={safeSelected}
              param={selected}
              defaultLocation={defaultParamLocation}
              onChange={(patch) => updateField(safeSelected, patch)}
              onRemove={() => removeField(safeSelected)}
            />
          )}
        </div>
      )}

      {/* ── Footer ──────────────────────────────────────────────── */}
      <div className="flex items-start gap-2 border-t border-border bg-app-bg/30 p-3 text-[11px] text-app-muted">
        <Info size={13} className="mt-0.5 shrink-0" />
        <div>
          Request params are optional unless marked as required.
          <br />
          Defaults for this method are sent in the {defaultParamLabel}; override location only for mixed query/body endpoints.
        </div>
      </div>

      <QuerySchemaHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} defaultLocation={defaultParamLabel} />
    </div>
  )
}

// Plain-language explainer. Goal: an operator who's never thought about
// query strings before should grok this in <30 seconds.
function QuerySchemaHelpDialog({ open, onClose, defaultLocation }: { open: boolean; onClose: () => void; defaultLocation: string }) {
  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <header className="flex items-start gap-3 border-b border-border px-6 py-4">
        <Info size={18} className="mt-0.5 shrink-0 text-app-muted" />
        <div className="flex-1">
          <h2 className="text-base font-semibold">What are request params?</h2>
          <p className="mt-0.5 text-xs text-app-muted">The list of optional inputs this endpoint accepts on top of the path.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1 rounded-control p-1.5 text-app-subtle transition hover:bg-white/5 hover:text-app-text">
          <X size={16} />
        </button>
      </header>
      <div className="space-y-4 px-6 py-5 text-sm text-app-text">
        <section>
          <p>
            The request params schema lists every optional input this endpoint accepts. Each entry has a
            name (like <code className="rounded bg-white/5 px-1">from</code> or <code className="rounded bg-white/5 px-1">status</code>),
            a type, and rules — required or not, allowed values, ranges, and so on.
          </p>
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">Who fills these in?</h3>
          <p className="text-xs text-app-muted">
            The AI agent. When a user is chatting with the agent, the agent figures out which
            values it needs (a date, a status, a limit), pulls them from the conversation, and
            sends them along when it calls this endpoint. By default, params for this method go in the {defaultLocation}.
            The schema is what teaches the agent
            which inputs are valid — it won't invent inputs that aren't listed here, and it won't
            send a value that breaks the type or range rules you set.
          </p>
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">What the request looks like</h3>
          <p className="text-xs text-app-muted">
            GET params become the part after the question mark. POST, PUT, and PATCH params
            are sent in the JSON request body unless a param explicitly sets location to query.
          </p>
          <pre className="mt-1 overflow-x-auto rounded-control border border-border bg-app-bg p-3 font-mono text-[11px] text-app-text">{`GET /api/v1/.../payouts_summary?from=2026-04-01&status=pending`}</pre>
          <pre className="mt-2 overflow-x-auto rounded-control border border-border bg-app-bg p-3 font-mono text-[11px] text-app-text">{`PATCH /api/v1/.../health
{ "weight_lbs": 170 }`}</pre>
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-app-subtle">Rules of thumb</h3>
          <ul className="list-disc space-y-1 pl-5 text-xs text-app-muted">
            <li>Mark a field <strong>required</strong> only when the endpoint will fail without it. Everything else is optional.</li>
            <li>Pick the right <strong>type</strong> (string, integer, number, boolean). The type prevents the agent from sending the wrong kind of value.</li>
            <li>Use <strong>enum</strong> when only a fixed set of values is allowed — for example, <code className="rounded bg-white/5 px-1">pending</code> or <code className="rounded bg-white/5 px-1">closed</code>.</li>
            <li>Write a clear <strong>description</strong>. The agent reads it and uses it to decide when this input is relevant.</li>
          </ul>
        </section>
      </div>
      <footer className="flex justify-end border-t border-border px-6 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-control border border-border px-4 py-1.5 text-sm text-app-text transition hover:bg-white/5"
        >
          Got it
        </button>
      </footer>
    </Dialog>
  )
}

// ─── Segmented toggle (Visual / Raw JSON) ─────────────────────────
function SegmentedToggle<K extends string>({
  value, onChange, options,
}: {
  value: K
  onChange: (next: K) => void
  options: Array<{ key: K; label: string }>
}) {
  return (
    <div role="tablist" className="inline-flex rounded-control border border-border bg-app-bg/60 p-0.5">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          role="tab"
          aria-selected={value === o.key}
          onClick={() => onChange(o.key)}
          className={cn(
            "rounded-control px-3 py-1 text-xs font-medium transition",
            value === o.key
              ? "bg-brand-red text-white shadow-redGlow"
              : "text-app-muted hover:text-app-text",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ─── Detail editor (right pane) ────────────────────────────────────
function DetailEditor({
  param, defaultLocation, onChange, onRemove,
}: {
  param: Param
  defaultLocation: "query" | "body"
  onChange: (patch: Partial<Param>) => void
  onRemove: () => void
}) {
  const isString  = (param.type ?? "") === "string" || !param.type
  const isNumeric = param.type === "integer" || param.type === "number"
  const enumText  = (param.enum ?? []).join(", ")

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-semibold text-app-text">Edit field</div>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Delete field"
          title="Delete"
          className="rounded-control border border-status-error/40 p-1.5 text-status-error transition hover:bg-status-error/10"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <Field label="name">
        <Text
          value={param.name}
          onChange={(v) => onChange({ name: v.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
          placeholder="snake_case"
          mono
        />
      </Field>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="type">
          <Select
            value={param.type ?? ""}
            onChange={(v) => onChange({ type: v as Param["type"] })}
            options={TYPE_OPTIONS}
          />
        </Field>
        <Field label="location">
          <Select
            value={param.location ?? ""}
            onChange={(v) => onChange({ location: (v || undefined) as Param["location"] })}
            options={LOCATION_OPTIONS.map((option) => option.key === "" ? { ...option, label: `default (${defaultLocation})` } : option)}
          />
        </Field>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="format (optional)">
          <Select
            value={param.format ?? ""}
            onChange={(v) => onChange({ format: v || undefined })}
            options={FORMAT_OPTIONS}
            disabled={!isString}
          />
        </Field>
      </div>

      <div>
        <div className="mb-1 text-[10px] uppercase tracking-wide text-app-subtle">required</div>
        <Toggle
          checked={!!param.required}
          onChange={(v) => onChange({ required: v })}
          labelOn="Required"
          labelOff="Optional (default)"
        />
      </div>

      {isNumeric && (
        <Field label="min / max">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <NumberInput
                value={param.minimum}
                onChange={(v) => onChange({ minimum: v })}
                placeholder="0"
              />
              <div className="mt-1 text-[10px] text-app-subtle">Min (optional)</div>
            </div>
            <div>
              <NumberInput
                value={param.maximum}
                onChange={(v) => onChange({ maximum: v })}
                placeholder="100"
              />
              <div className="mt-1 text-[10px] text-app-subtle">Max (optional)</div>
            </div>
          </div>
        </Field>
      )}

      {isString && (
        <Field label="pattern (regex)">
          <Text
            value={param.pattern ?? ""}
            onChange={(v) => onChange({ pattern: v || undefined })}
            placeholder="^[A-Z]{3}$"
            mono
          />
        </Field>
      )}

      <Field
        label="enum / options"
        hint={isString ? "Comma-separated values (for string types)" : "Comma-separated values"}
        counter={`${enumText.length} / 1000`}
      >
        <Text
          value={enumText}
          onChange={(v) => {
            const list = v.split(",").map((s) => s.trim()).filter(Boolean)
            onChange({ enum: list.length ? list : undefined })
          }}
          placeholder="e.g. pending, active, closed"
          maxLength={1000}
        />
      </Field>

      <Field
        label="description (optional)"
        counter={`${(param.description ?? "").length} / 1000`}
      >
        <Textarea
          value={param.description ?? ""}
          onChange={(v) => onChange({ description: v || undefined })}
          placeholder="Range start (ISO8601 UTC)."
          maxLength={1000}
          rows={2}
        />
      </Field>
    </div>
  )
}

// ─── Field wrapper + primitives ────────────────────────────────────
function Field({
  label, hint, counter, children,
}: {
  label: string
  hint?: string
  counter?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wide text-app-subtle">{label}</div>
      {children}
      {(hint || counter) && (
        <div className="mt-1 flex items-center justify-between text-[10px] text-app-subtle">
          <span>{hint}</span>
          {counter && <span>{counter}</span>}
        </div>
      )}
    </div>
  )
}

const INPUT_BASE = "w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text placeholder:text-app-subtle focus:border-brand-red/60 focus:outline-none disabled:opacity-40"

function Text({
  value, onChange, placeholder, mono, maxLength,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  mono?: boolean
  maxLength?: number
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className={cn(INPUT_BASE, mono && "font-mono")}
    />
  )
}

function Textarea({
  value, onChange, placeholder, rows = 3, maxLength,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  rows?: number
  maxLength?: number
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className={cn(INPUT_BASE, "resize-none")}
    />
  )
}

function NumberInput({
  value, onChange, placeholder,
}: {
  value: number | undefined
  onChange: (v: number | undefined) => void
  placeholder?: string
}) {
  return (
    <input
      type="number"
      value={value ?? ""}
      onChange={(e) => {
        const v = e.target.value
        onChange(v === "" ? undefined : globalThis.Number(v))
      }}
      placeholder={placeholder}
      className={INPUT_BASE}
    />
  )
}

function Select({
  value, onChange, options, disabled,
}: {
  value: string
  onChange: (v: string) => void
  options: Array<{ key: string; label: string }>
  disabled?: boolean
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={INPUT_BASE}
    >
      {options.map((o) => (
        <option key={o.key} value={o.key}>{o.label}</option>
      ))}
    </select>
  )
}

function Toggle({
  checked, onChange, labelOn, labelOff,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  labelOn: string
  labelOff: string
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative h-5 w-9 rounded-full border transition",
          checked
            ? "border-brand-red/60 bg-brand-red"
            : "border-border bg-app-bg",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition",
            checked ? "left-[18px]" : "left-0.5",
          )}
        />
      </button>
      <span className="text-xs text-app-muted">{checked ? labelOn : labelOff}</span>
    </label>
  )
}
